// import
import * as ReadyOnlyType from "../../components/FieldTypes/TypeReadOnly"
import PropTypes from "prop-types"
import { DATE_SUBTYPES, transformDate } from "../../helpers/dateNormalization"

export default function FieldValuePerType({
  fieldValuePerTypeArg,
  block_title,
}) {
  const {
    blockValues,
    fieldName,
    fieldType,
    fieldSubtype,
    fieldOptions,
    description,
    multi_fields,
  } = fieldValuePerTypeArg

  const displayFormat =
    fieldSubtype === DATE_SUBTYPES.TIME
      ? "hh:mm a"
      : fieldSubtype === DATE_SUBTYPES.DATETIME
        ? "MM/dd/yyyy hh:mm a"
        : "MM/dd/yyyy"

  switch (fieldType) {
    case "checkbox":
      return (
        <ReadyOnlyType.TypeCheckbox
          value={blockValues[fieldName]}
          name={fieldName}
        />
      )
    case "date":
    case "conditional_date":
      const input =
        transformDate(
          blockValues[fieldName],
          fieldType,
          fieldSubtype,
          true,
        )?.toFormat?.(displayFormat) || "—"
      return <ReadyOnlyType.TypeText value={input} name={fieldName} />
    case "dropdown":
      return (
        <ReadyOnlyType.TypeDropdown
          value={blockValues[fieldName]}
          options={fieldOptions}
          description={description}
          name={fieldName}
        />
      )
    case "multiselect":
      return (
        <ReadyOnlyType.TypeMultiSelect
          value={blockValues[fieldName]}
          name={fieldName}
        />
      )
    case "dropdown_us_states":
    case "conditional_dropdown_us_states":
      return (
        <ReadyOnlyType.TypeState
          value={blockValues[fieldName]}
          name={fieldName}
        />
      )
    case "email":
      return (
        <ReadyOnlyType.TypeEmail
          value={blockValues[fieldName]}
          name={fieldName}
        />
      )
    case "monthpicker":
      return (
        <ReadyOnlyType.TypeMonthPicker
          value={blockValues[fieldName]}
          name={fieldName}
        />
      )
    case "multi_field":
      return (
        <ReadyOnlyType.TypeMultiField
          value={blockValues[fieldName]}
          name={fieldName}
          multi_fields={multi_fields}
        />
      )
    case "text":
    case "conditional_phone":
      return (
        <ReadyOnlyType.TypeText
          value={blockValues[fieldName]}
          name={fieldName}
        />
      )
    case "textarea":
    case "conditional_sys":
      if (blockValues["membership__staff_id_selector"]) {
        return (
          <ReadyOnlyType.TypeDropdown
            value={blockValues[fieldName]}
            options={fieldOptions}
            description={description}
            name={fieldName}
          />
        )
      }
      return (
        <ReadyOnlyType.TypeTextarea
          value={blockValues[fieldName]}
          name={fieldName}
        />
      )
    case "textarea_rich":
      return (
        <ReadyOnlyType.TypeTextareaRich
          value={blockValues[fieldName]}
          name={fieldName}
        />
      )
    case "time": {
      const input =
        transformDate(
          blockValues[fieldName],
          "time",
          DATE_SUBTYPES.TIME,
          true,
        )?.toFormat?.("hh:mm a") || "—"
      return <ReadyOnlyType.TypeText value={input} name={fieldName} />
    }
    case "sys":
      switch (fieldName) {
        case "demo_birthdate":
        case "account_birthdate":
        case "form_date":
        case "session_date":
        case "activity_date":
          const input =
            transformDate(
              blockValues[fieldName],
              fieldType,
              fieldSubtype,
              true,
            )?.toFormat?.(displayFormat) || "—"
          return <ReadyOnlyType.TypeText value={input} name={fieldName} />
        case "group_supervision__program_identity_ids":
        case "session__program_identity_ids":
        case "manage_services_type":
        case "manage_staff_id_selector":
        case "manage_location_ids":
        case "manage_program_identity_ids":
        case "activity__program_identity_ids":
          return (
            <ReadyOnlyType.TypeDropdown
              value={blockValues[fieldName]}
              options={fieldOptions}
              description={description}
              name={fieldName}
            />
          )
        case "session_start_time":
        case "form_start_time":
          return (
            <ReadyOnlyType.TypeTime
              value={blockValues[fieldName]}
              description={description}
            />
          )
        case "demo_ssn":
          let value = blockValues[fieldName]
          let renderFieldName = fieldName

          if (!value) {
            renderFieldName = fieldName + "_not_set"
          }

          return <ReadyOnlyType.TypeText value={value} name={renderFieldName} />
        case "form__location_ids":
        case "session__location_ids":
          const locations = blockValues[fieldName]

          // If locations is not an array or is empty, return "No locations"
          if (!Array.isArray(locations) || locations.length === 0) {
            return (
              <ReadyOnlyType.TypeText value="No locations" name={fieldName} />
            )
          }

          const locationLabels = locations
            .map((location) => location.label)
            .join(", ")
          return (
            <ReadyOnlyType.TypeText value={locationLabels} name={fieldName} />
          )
        case "assessments":
          //Return null if no value to keep aligned with original behavior
          if (!blockValues[fieldName]) {
            return null
          }

          return (
            <ReadyOnlyType.TypeAssesment
              value={blockValues[fieldName]}
              name={fieldName}
              fieldName={fieldName}
            />
          )
        case "staff_phone_telerecovery":
          return (
            <ReadyOnlyType.TypePhone
              value={blockValues[fieldName]}
              name={fieldName}
            />
          )
        case "staff_primary_supervisor":
        case "staff_role":
          return (
            <ReadyOnlyType.TypeDropdown
              value={blockValues[fieldName]}
              options={fieldOptions}
              description={description}
              name={fieldName}
            />
          )
        default:
          return null
      }
    case "hidden":
      if (blockValues["session_length"]) {
        return (
          <ReadyOnlyType.TypeText value={blockValues[fieldName] + " minutes"} />
        )
      } else {
        return null
      }

    case "number":
      switch (block_title) {
        case "Participant Outcomes": {
          return (
            <ReadyOnlyType.TypeNumberOutcomes
              value={blockValues[fieldName]}
              description={description}
              name={fieldName}
            />
          )
        }

        default: {
          return (
            <ReadyOnlyType.TypeNumber
              value={blockValues[fieldName]}
              description={description}
              name={fieldName}
            />
          )
        }
      }

    case "password":
      return {}

    case "radio":
      switch (block_title) {
        case "Participant Outcomes": {
          return (
            <ReadyOnlyType.TypeRadioOutcomes
              value={blockValues[fieldName]}
              description={description}
              name={fieldName}
              options={fieldOptions}
            />
          )
        }

        default: {
          return (
            <ReadyOnlyType.TypeRadio
              value={blockValues[fieldName]}
              description={description}
              name={fieldName}
              options={fieldOptions}
            />
          )
        }
      }

    case "phone":
      return (
        <ReadyOnlyType.TypePhone
          value={blockValues[fieldName]}
          name={fieldName}
        />
      )
    case "url":
      return (
        <ReadyOnlyType.TypeURL
          value={blockValues[fieldName]}
          name={fieldName}
        />
      )
    case "zipcode":
      return (
        <ReadyOnlyType.TypeZipCode
          value={blockValues[fieldName]}
          name={fieldName}
        />
      )
    case "dropdowngrouped":
      return (
        <ReadyOnlyType.TypeDropDownGrouped
          value={blockValues[fieldName]}
          name={fieldName}
        />
      )
    case "conditional_text": {
      switch (block_title) {
        case "Participant Outcomes": {
          return (
            <ReadyOnlyType.TypeConditionalTextOutcomes
              value={blockValues[fieldName]}
              description={description}
              name={fieldName}
            />
          )
        }

        default: {
          return (
            <ReadyOnlyType.TypeConditionalText
              value={blockValues[fieldName]}
              description={description}
              name={fieldName}
            />
          )
        }
      }
    }

    case "conditional_email":
      return (
        <ReadyOnlyType.TypeConditionalText
          value={blockValues[fieldName]}
          description={description}
          name={fieldName}
        />
      )

    case "conditional_textarea":
      return (
        <ReadyOnlyType.TypeConditionalTextarea
          value={blockValues[fieldName]}
          description={description}
          name={fieldName}
        />
      )
    case "conditional_number":
      switch (block_title) {
        case "Participant Outcomes": {
          return (
            <ReadyOnlyType.TypeConditionalNumberOutcomes
              value={blockValues[fieldName]}
              description={description}
              name={fieldName}
            />
          )
        }

        default: {
          return (
            <ReadyOnlyType.TypeConditionalNumber
              value={blockValues[fieldName]}
              description={description}
              name={fieldName}
            />
          )
        }
      }

    case "conditional_checkbox":
      return (
        <ReadyOnlyType.TypeConditionalCheckbox
          value={blockValues[fieldName]}
          name={fieldName}
        />
      )
    case "conditional_dropdown":
      return (
        <ReadyOnlyType.TypeConditionalDropdown
          value={blockValues[fieldName]}
          name={fieldName}
        />
      )
    case "file":
    case "file_image":
      return (
        <ReadyOnlyType.TypeFile
          value={blockValues[fieldName]}
          name={fieldName}
          type={fieldType}
        />
      )
    default:
      return null
  }
}

FieldValuePerType.propTypes = {
  fieldValuePerTypeArg: PropTypes.object,
  block_title: PropTypes.string,
}
